import moment from "moment";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { getExamAttemptDetailsById } from "../../../../../api/services/exam";
import { handleApiError } from "../../../../../utils/errorHandler";
import { pushNotification } from "../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { ExamAttemptDetails } from "../../../../../api/interfaces/Exam";
import { useNavigate } from "react-router-dom";

interface ExamAttemptDetailsProps {
    examCodeName: string;
    examAttemptId: string;
}

const FieldSkeleton = () => (
    <Box sx={{ mb: 2 }}>
        <Skeleton variant="text" width={160} height={16} />
        <Skeleton variant="text" width="60%" height={20} />
    </Box>
);

const ExamAttemptDetailsDrawer: React.FC<ExamAttemptDetailsProps> = ({ examCodeName, examAttemptId }) => {
    const navigate = useNavigate();
    const [examAttempt, setExamAttempt] = useState<ExamAttemptDetails | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const run = async () => {
            if (!examAttemptId) return;
            setLoading(true);
            try {
                const examLists = await getExamAttemptDetailsById(examAttemptId);
                setExamAttempt(examLists.data);
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
            } finally {
                setLoading(false);
            }

        }
        run();
    }, [examAttemptId]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            
        {loading ? (
            <>
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />

                <Box sx={{ mb: 2 }}>
                    <Skeleton variant="text" width={160} height={16} />
                    <Skeleton variant="rectangular" width={140} height={32} />
                </Box>
            </>
        ) : (
            <>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 400, color: '#5840BA', mb: 1 }}>Exam Name:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#373737' }}>{examAttempt?.examName}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 400, color: '#5840BA', mb: 1 }}>Exam Code:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#373737' }}>{examCodeName}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 400, color: '#5840BA', mb: 1 }}>Duration:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#373737' }}>{examAttempt?.timer} min{examAttempt && examAttempt.timer !== 1 && 's'}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 400, color: '#5840BA', mb: 1 }}>Date Taken:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#373737' }}>
                        {examAttempt?.dateTaken ? moment(examAttempt.dateTaken).format("MMMM DD, YYYY") : "-"}
                    </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 400, color: '#5840BA', mb: 1 }}>Attempt No:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#373737' }}>{examAttempt?.attemptNumber}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 400, color: '#5840BA', mb: 1 }}>Examination Link:</Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        endIcon={<DescriptionOutlinedIcon sx={{ fontSize: "14px" }} />}
                        sx={{
                            borderRadius: "4px",
                            borderColor: "#5840BA",
                            color: "#5840BA",
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: 400,
                            backgroundColor: "#FFFFFF",
                            minWidth: "90px",
                            "&:hover": {
                                backgroundColor: "#F7F5FF",
                                borderColor: "#5840BA",
                            },
                        }}
                        onClick={() => navigate(`/exam/${examAttempt?._id}/view`)}
                    >
                        Check My Exam
                    </Button>
                </Box>
            </>
        )}
        </Box>
    );
};

export default ExamAttemptDetailsDrawer;